import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ScrollText, ShieldCheck, RefreshCw, Loader2, Calendar,
  Building2, ChevronDown, ChevronRight, CheckCircle2, XCircle, AlertTriangle, Info,
  FileDown, Radar,
} from "lucide-react";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";

const PRODUCT_ICON = {
  "processo-timeline": ScrollText,
  "compliance-360": ShieldCheck,
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric",
                                         hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtCurrency(cents, cur = "brl") {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: (cur || "brl").toUpperCase() })
    .format((cents || 0) / 100);
}

export default function IntelligenceOrders() {
  const [params, setParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busyDetail, setBusyDetail] = useState(false);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const r = await api.get("/intel/orders/mine");
      setOrders(r.data.items || []);
    } catch (e) {
      toast.error("Falha ao carregar pedidos");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadOrders(); }, []);

  useEffect(() => {
    const initial = params.get("open") || params.get("order");
    if (initial) {
      setOpenId(initial);
      params.delete("open"); params.delete("order");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    let alive = true;
    const load = async () => {
      try {
        setBusyDetail(true);
        const r = await api.get(`/intel/orders/${openId}`);
        if (alive) setDetail(r.data);
      } catch (e) {
        toast.error("Não foi possível carregar o pedido");
      } finally { if (alive) setBusyDetail(false); }
    };
    load();
    // Poll a cada 5s enquanto não estiver ready — cobre o gap do webhook
    const t = setInterval(() => {
      if (detail?.order && ["ready", "delivered", "failed", "refunded"].includes(detail.order.status)) return;
      load();
    }, 5000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line
  }, [openId, detail?.order?.status]);

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="intel-orders-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/inteligencia" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3"/> Voltar
            </Link>
            <h1 className="mt-1 font-heading text-3xl font-semibold" style={{ color: NAVY }}>
              Meus pedidos · Inteligência 360
            </h1>
            <p className="text-sm text-slate-500">Todos os pedidos ficam auditados aqui. Relatórios só liberam após pagamento confirmado.</p>
          </div>
          <button onClick={loadOrders} data-testid="refresh-orders"
                  className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> Atualizar
          </button>
        </div>

        {loading && orders.length === 0 ? (
          <SkeletonList/>
        ) : orders.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
            Nenhum pedido ainda. Volte à <Link to="/inteligencia" className="underline">página principal</Link> e faça sua primeira consulta.
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-4">
            {/* Lista */}
            <div className="space-y-2" data-testid="orders-list">
              {orders.map(o => {
                const Icon = PRODUCT_ICON[o.product_slug] || Building2;
                const active = openId === o.order_id;
                return (
                  <button key={o.order_id}
                    onClick={() => setOpenId(o.order_id)}
                    data-testid={`order-row-${o.order_id}`}
                    className={`w-full text-left bg-white rounded-xl border p-3 transition
                      ${active ? "border-slate-800 shadow-md" : "border-slate-200 hover:border-slate-400"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                           style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}>
                        <Icon className="w-4 h-4" style={{ color: GOLD }}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">
                          {o.product_slug}
                        </div>
                        <div className="font-mono text-xs text-slate-500 truncate">{o.order_id}</div>
                        <div className="text-sm truncate" style={{ color: NAVY }}>{o.identifier_input}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusBadge status={o.status}/>
                          <span className="text-[11px] text-slate-500">
                            {fmtCurrency(o.total_cents, o.currency)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 mt-1"/>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Detalhe */}
            <div className="bg-white rounded-2xl border border-slate-200 min-h-[400px]" data-testid="order-detail">
              {!openId && (
                <div className="p-8 text-center text-slate-500">
                  Selecione um pedido à esquerda para ver o relatório.
                </div>
              )}
              {openId && (busyDetail || !detail) && (
                <div className="p-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2"/> Carregando…
                </div>
              )}
              {openId && detail && !busyDetail && (
                <OrderDetail detail={detail}/>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2">
      {[1,2,3].map(i => (
        <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
          <div className="h-3 w-20 bg-slate-200 rounded mb-2"/>
          <div className="h-4 w-64 bg-slate-100 rounded"/>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    ready:   { c: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "PRONTO" },
    delivered: { c: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "ENTREGUE" },
    processing: { c: "bg-blue-50 text-blue-700 border-blue-300", label: "PROCESSANDO" },
    paid: { c: "bg-blue-50 text-blue-700 border-blue-300", label: "PAGO" },
    awaiting_payment: { c: "bg-amber-50 text-amber-700 border-amber-300", label: "AGUARDANDO PGTO" },
    draft: { c: "bg-slate-50 text-slate-500 border-slate-300", label: "RASCUNHO" },
    failed: { c: "bg-rose-50 text-rose-700 border-rose-300", label: "FALHOU" },
    refunded: { c: "bg-slate-50 text-slate-500 border-slate-300", label: "REEMBOLSADO" },
  };
  const s = map[status] || { c: "bg-slate-50 text-slate-500 border-slate-300", label: (status || "").toUpperCase() };
  return (
    <span className={`inline-block text-[10px] tracking-widest border rounded px-1.5 py-0.5 ${s.c}`}
          data-testid={`status-${status}`}>
      {s.label}
    </span>
  );
}

function OrderDetail({ detail }) {
  const { order, payload } = detail;
  const isTimeline = order.product_slug === "processo-timeline";
  const isCompliance = order.product_slug === "compliance-360";
  const canDownload = order.status === "ready" || order.status === "delivered";
  const [pdfBusy, setPdfBusy] = useState(false);
  const [watchBusy, setWatchBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/intel/orders/${order.order_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `hermes-intel-${order.order_id}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
    } catch (e) {
      toast.error("Falha ao gerar PDF");
    } finally { setPdfBusy(false); }
  };

  const enableWatch = async () => {
    setWatchBusy(true);
    try {
      await api.post("/intel/watches", { cnj: order.identifier_input });
      toast.success("Watch grátis ativada — abrindo lista de watches");
      setTimeout(() => { window.location.href = "/inteligencia/watches"; }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao ativar watch");
    } finally { setWatchBusy(false); }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Pedido</div>
          <div className="font-mono text-xs text-slate-500">{order.order_id}</div>
          <div className="font-heading text-xl font-semibold mt-1" style={{ color: NAVY }}>
            {(order.product_snapshot?.name) || order.product_slug}
          </div>
          <div className="text-sm text-slate-600 mt-0.5">
            Identificador: <b>{order.identifier_input}</b>
          </div>
        </div>
        <div className="text-right">
          <StatusBadge status={order.status}/>
          <div className="text-sm mt-1 text-slate-500">{fmtCurrency(order.total_cents, order.currency)}</div>
          <div className="text-[11px] text-slate-400 mt-1">Criado {fmtDate(order.created_at)}</div>
        </div>
      </div>

      {/* Ações liberadas após ready */}
      {canDownload && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={downloadPdf}
            disabled={pdfBusy}
            data-testid="btn-download-pdf"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: NAVY }}>
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <FileDown className="w-4 h-4"/>}
            Baixar PDF
          </button>
          {isTimeline && (
            <button onClick={enableWatch}
              disabled={watchBusy}
              data-testid="btn-enable-watch"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-300 hover:bg-slate-100">
              {watchBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Radar className="w-4 h-4"/>}
              Ativar Processo Watch
            </button>
          )}
        </div>
      )}

      {/* Ainda não liberado */}
      {order.status !== "ready" && order.status !== "delivered" && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {order.status === "awaiting_payment" && (
            <>Aguardando confirmação do pagamento pelo Stripe. O relatório aparece assim que o webhook confirmar (segundos após a compra).</>
          )}
          {order.status === "processing" && (
            <>Pagamento confirmado. Consultando fontes oficiais agora — atualize em alguns segundos.</>
          )}
          {order.status === "failed" && (
            <><b>Falha no fulfillment.</b> Nenhuma cobrança será mantida — se persistir, contate o suporte.</>
          )}
          {order.status === "refunded" && (
            <>Este pedido foi reembolsado. Relatório indisponível.</>
          )}
          {["draft","paid"].includes(order.status) && (
            <>Pedido em <b>{order.status}</b>. Aguardando processamento.</>
          )}
        </div>
      )}

      {/* Payload liberado */}
      {payload && (order.status === "ready" || order.status === "delivered") && (
        <div className="mt-6" data-testid="order-payload">
          {isTimeline && <TimelinePayload payload={payload}/>}
          {isCompliance && <CompliancePayload payload={payload}/>}
          {order.product_slug === "cnpj-360" && <Cnpj360Payload payload={payload}/>}
          {!isTimeline && !isCompliance && order.product_slug !== "cnpj-360" && (
            <pre className="text-xs bg-slate-50 border rounded-lg p-3 overflow-auto max-h-[400px]">
              {JSON.stringify(payload, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Histórico */}
      <details className="mt-6 rounded-lg border border-slate-200 bg-slate-50">
        <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest text-slate-500">
          Histórico de status ({(order.history || []).length})
        </summary>
        <div className="p-3 space-y-1.5 text-xs">
          {(order.history || []).map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-mono text-slate-400">{fmtDate(h.at)}</span>
              <span className="font-semibold text-slate-700">{h.status}</span>
              {h.meta?.stripe_event && <span className="text-slate-400">· {h.meta.stripe_event.slice(0,20)}…</span>}
              {h.meta?.error && <span className="text-rose-500">· {h.meta.error.slice(0,60)}</span>}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

/* -------- Payload · Processo Timeline (DataJud) -------- */
function TimelinePayload({ payload }) {
  if (payload.status === "NOT_FOUND") {
    return <SourceMsg icon={Info} tone="slate"
      title="Processo não encontrado no DataJud"
      msg="O DataJud não retornou nenhum resultado para este CNJ nos tribunais consultados."/>;
  }
  if (payload.status === "SOURCE_UNAVAILABLE") {
    return <SourceMsg icon={AlertTriangle} tone="amber"
      title="Fonte oficial temporariamente indisponível"
      msg={payload.reason || "DataJud sem resposta"}/>;
  }
  if (payload.status === "NOT_QUERIED") {
    return <SourceMsg icon={XCircle} tone="rose"
      title="CNJ inválido"
      msg={payload.reason || "Verifique o número informado"}/>;
  }
  const movimentos = payload.movimentos || [];
  return (
    <div>
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-center gap-2 text-emerald-800">
          <CheckCircle2 className="w-4 h-4"/>
          <div className="text-xs uppercase tracking-widest font-bold">Encontrado</div>
        </div>
        <div className="mt-2 grid md:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <Field label="Número" value={payload.numero_processo}/>
          <Field label="Classe" value={payload.classe}/>
          <Field label="Órgão julgador" value={payload.orgao_julgador}/>
          <Field label="Grau" value={payload.grau}/>
          <Field label="Sistema" value={payload.sistema}/>
          <Field label="Formato" value={payload.formato}/>
          <Field label="Data ajuizamento" value={fmtDate(payload.data_ajuizamento)}/>
          <Field label="Última atualização" value={fmtDate(payload.ultima_atualizacao)}/>
        </div>
        {(payload.assuntos || []).length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Assuntos</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {payload.assuntos.map((a,i) => (
                <span key={i} className="text-[11px] bg-white border border-emerald-200 rounded px-1.5 py-0.5">{a}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
          Movimentos oficiais ({movimentos.length})
        </div>
        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
          {movimentos.slice(0,50).map((m, i) => (
            <li key={i} className="ml-4">
              <div className="absolute -left-[7px] w-3 h-3 rounded-full bg-white border-2"
                   style={{ borderColor: GOLD }}/>
              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                <Calendar className="w-3 h-3"/> {fmtDate(m.data_hora)}
              </div>
              <div className="text-sm font-medium" style={{ color: NAVY }}>{m.nome || "—"}</div>
              {m.orgao && <div className="text-xs text-slate-500">{m.orgao}</div>}
              {(m.complementos || []).length > 0 && (
                <div className="text-[11px] text-slate-500 mt-0.5">{m.complementos.join(" · ")}</div>
              )}
            </li>
          ))}
        </ol>
        {movimentos.length > 50 && (
          <div className="text-xs text-slate-500 mt-2">+ {movimentos.length - 50} movimentos anteriores</div>
        )}
      </div>

      <SourceFooter name={payload.source_name} at={payload.retrieved_at}/>
    </div>
  );
}

/* -------- Payload · Compliance 360 (CGU) -------- */
function CompliancePayload({ payload }) {
  const summary = payload.summary || {};
  const positive = (summary.total_occurrences || 0) > 0;
  return (
    <div>
      <div className={`rounded-xl border p-4 ${positive ? "border-rose-300 bg-rose-50" : "border-emerald-300 bg-emerald-50"}`}>
        <div className="flex items-center gap-2">
          {positive ? <AlertTriangle className="w-4 h-4 text-rose-700"/> : <CheckCircle2 className="w-4 h-4 text-emerald-700"/>}
          <div className={`text-xs uppercase tracking-widest font-bold ${positive ? "text-rose-800" : "text-emerald-800"}`}>
            {summary.verdict || "SEM OCORRÊNCIA IDENTIFICADA"}
          </div>
        </div>
        <div className="text-sm mt-1" style={{ color: NAVY }}>
          <b>{payload.tipo}</b> {payload.identifier} · Total de ocorrências: <b>{summary.total_occurrences || 0}</b>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-3">
        {Object.entries(payload.sources || {}).map(([key, src]) => (
          <div key={key} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500">{key}</div>
              <SourceStatusPill status={src.status} count={src.count}/>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{src.source_name}</div>
            {(src.records || []).length > 0 ? (
              <ul className="mt-2 text-xs space-y-1">
                {src.records.slice(0, 3).map((r, i) => (
                  <li key={i} className="bg-slate-50 border border-slate-200 rounded p-2">
                    <pre className="text-[10px] whitespace-pre-wrap break-words">{JSON.stringify(r, null, 2).slice(0, 500)}</pre>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-[11px] text-slate-500">Sem registros nesta fonte.</div>
            )}
          </div>
        ))}
      </div>

      {payload.disclaimer && (
        <div className="mt-4 text-[11px] text-slate-500 italic">{payload.disclaimer}</div>
      )}
      <SourceFooter name="CGU · Portal da Transparência" at={payload.consulted_at}/>
    </div>
  );
}

function SourceStatusPill({ status, count }) {
  const map = {
    FOUND: { c: "bg-rose-50 text-rose-700 border-rose-300", label: `+${count || 0}` },
    NOT_FOUND: { c: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "OK" },
    SOURCE_UNAVAILABLE: { c: "bg-amber-50 text-amber-700 border-amber-300", label: "INDISP." },
    NOT_QUERIED: { c: "bg-slate-50 text-slate-500 border-slate-300", label: "N/A" },
  };
  const s = map[status] || map.NOT_QUERIED;
  return (
    <span className={`text-[10px] tracking-widest border rounded px-1.5 py-0.5 ${s.c}`}>
      {s.label}
    </span>
  );
}

function SourceMsg({ icon: Icon, tone, title, msg }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose:  "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2"><Icon className="w-4 h-4"/><b>{title}</b></div>
      <div className="text-sm mt-1">{msg}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm" style={{ color: NAVY }}>{value || "—"}</div>
    </div>
  );
}

function SourceFooter({ name, at }) {
  return (
    <div className="mt-5 pt-3 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-2">
      <Info className="w-3 h-3"/>
      Fonte: <b>{name || "—"}</b> · Consultado em {fmtDate(at)}
    </div>
  );
}

/* -------- Payload · CNPJ 360 (Fase 3) -------- */
function Cnpj360Payload({ payload }) {
  const rfb = payload.rfb || {};
  const compliance = payload.compliance || {};
  const pgfn = payload.pgfn || {};
  const summary = payload.summary || {};

  return (
    <div>
      {/* Header verdict */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Veredito consolidado</div>
        <div className="text-lg font-semibold" style={{ color: NAVY }}>{summary.verdict}</div>
        <div className="text-sm text-slate-600 mt-1">
          {summary.sources_found}/{summary.sources_queried} fontes retornaram dados
        </div>
      </div>

      {/* RFB */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>RFB · Receita Federal</div>
            <div className="text-lg font-semibold" style={{ color: NAVY }}>{rfb.razao_social || "—"}</div>
            {rfb.nome_fantasia && <div className="text-sm text-slate-500">{rfb.nome_fantasia}</div>}
          </div>
          <span className={`text-[10px] tracking-widest border rounded px-1.5 py-0.5 ${
            rfb.situacao_cadastral === "ATIVA"
              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
              : "bg-amber-50 text-amber-700 border-amber-300"}`}>
            {rfb.situacao_cadastral || "—"}
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-2 text-sm mt-3">
          <Field label="CNPJ" value={rfb.cnpj}/>
          <Field label="Porte" value={rfb.porte}/>
          <Field label="Natureza jurídica" value={rfb.natureza_juridica}/>
          <Field label="Capital social" value={rfb.capital_social ? `R$ ${Number(rfb.capital_social).toLocaleString("pt-BR")}` : "—"}/>
          <Field label="CNAE fiscal" value={rfb.cnae_fiscal}/>
          <Field label="Início atividade" value={fmtDate(rfb.data_inicio_atividade)}/>
        </div>
        {rfb.endereco && (
          <div className="mt-3 text-sm">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Endereço</span>
            <div className="text-slate-700">
              {[rfb.endereco.logradouro, rfb.endereco.numero, rfb.endereco.complemento]
                .filter(Boolean).join(", ")}
              {rfb.endereco.bairro && ` · ${rfb.endereco.bairro}`}
              {rfb.endereco.municipio && ` · ${rfb.endereco.municipio}/${rfb.endereco.uf}`}
              {rfb.endereco.cep && ` · CEP ${rfb.endereco.cep}`}
            </div>
          </div>
        )}
        {(rfb.socios || []).length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              Sócios · QSA ({rfb.socios.length})
            </div>
            <div className="space-y-1">
              {rfb.socios.slice(0, 10).map((s, i) => (
                <div key={i} className="text-sm border border-slate-200 rounded px-2 py-1 bg-slate-50">
                  <b>{s.nome}</b>
                  {s.qualificacao && <span className="text-slate-500"> · {s.qualificacao}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Compliance summary */}
      {compliance.summary && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>Compliance CGU</div>
          <div className={`text-lg font-semibold ${
            (compliance.summary.total_occurrences || 0) > 0 ? "text-rose-700" : "text-emerald-700"
          }`}>
            {compliance.summary.verdict}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {compliance.summary.total_occurrences} ocorrência(s) totais nas 4 fontes CGU
          </div>
        </div>
      )}

      {/* PGFN skeleton */}
      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700"/>
          <div className="text-xs uppercase tracking-widest font-bold text-amber-800">
            PGFN Dívida Ativa · {pgfn.status}
          </div>
        </div>
        <div className="text-sm mt-1 text-amber-900">{pgfn.reason}</div>
      </div>

      {payload.disclaimer && (
        <div className="mt-4 text-[11px] text-slate-500 italic">{payload.disclaimer}</div>
      )}
      <SourceFooter name="Receita Federal (BrasilAPI) + CGU Portal da Transparência"
                     at={payload.consulted_at}/>
    </div>
  );
}
